import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NotificationBell } from '@/components/realtime/notification-bell';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useAuth } from '@/contexts/auth-context';
import { Link } from 'react-router-dom';
import {
  Home,
  BookOpen,
  Video,
  User,
  BarChart3,
  Users,
  GraduationCap,
  LogOut,
  Settings,
  Calendar,
  UserPlus,
  FileCheck,
  FolderTree,
  Shield,
  UserCog
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { RoleSwitcher } from './role-switcher';

export function MobileHeader() {
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;

  const getNavItems = () => {
    if (profile?.role === 'admin') {
      return [
        { href: '/dashboard', label: 'Dashboard', icon: Home },
        { href: '/dashboard/courses', label: 'Gestione Corsi', icon: BookOpen },
        { href: '/dashboard/users', label: 'Gestione Utenti', icon: Users },
        { href: '/dashboard/teacher-requests', label: 'Richieste Docenti', icon: FileCheck },
        { href: '/dashboard/payments', label: 'Pagamenti', icon: BarChart3 },
        { href: '/dashboard/categories', label: 'Categorie', icon: FolderTree },
        { href: '/dashboard/privacy', label: 'Privacy', icon: Shield },
        { href: '/dashboard/settings', label: 'Impostazioni', icon: Settings },
        { href: '/dashboard/account', label: 'Account', icon: UserCog },
      ];
    }
    if (profile?.role === 'docente') {
      return [
        { href: '/dashboard', label: 'Dashboard', icon: Home },
        { href: '/dashboard/my-courses', label: 'I miei corsi', icon: BookOpen },
        { href: '/dashboard/create-course', label: 'Crea corso', icon: GraduationCap },
        { href: '/dashboard/live', label: 'Live sessions', icon: Video },
        { href: '/dashboard/earnings', label: 'Guadagni', icon: BarChart3 },
        { href: '/dashboard/profile', label: 'Profilo', icon: User },
        { href: '/dashboard/account', label: 'Account', icon: UserCog },
      ];
    }
    const studentItems = [
      { href: '/dashboard', label: 'Home', icon: Home },
      { href: '/dashboard/courses', label: 'Catalogo corsi', icon: BookOpen },
      { href: '/dashboard/calendar', label: 'Calendario', icon: Calendar },
      { href: '/dashboard/my-learning', label: 'I miei corsi', icon: Video },
    ];

    if (!profile?.is_teacher) {
      studentItems.push({ href: '/dashboard/become-teacher', label: 'Diventa Docente', icon: UserPlus });
    }

    studentItems.push(
      { href: '/dashboard/profile', label: 'Profilo', icon: User },
      { href: '/dashboard/account', label: 'Account', icon: UserCog }
    );

    return studentItems;
  };

  const navItems = getNavItems();

  return (
    <header className="sticky top-0 z-40 w-full bg-white border-b border-gray-200 md:hidden">
      <div className="flex items-center justify-between h-16 px-4">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72">
            <SheetHeader>
              <SheetTitle className="flex items-center text-left">
                <GraduationCap className="w-6 h-6 text-blue-600 mr-2" />
                LMS Platform
              </SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col mt-6 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.href || pathname?.startsWith(item.href + '/');
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            <div className="absolute bottom-4 left-4 right-4">
              <div className="p-4 bg-gray-50 rounded-lg mb-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {profile?.full_name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {profile?.full_name || 'Utente'}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">{profile?.role}</p>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={signOut}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex items-center">
          <GraduationCap className="w-6 h-6 text-blue-600 mr-2" />
          <span className="text-lg font-bold text-gray-900">LMS</span>
        </div>

        <div className="flex items-center gap-2">
          <RoleSwitcher />
          <NotificationBell />
        </div>
      </div>
    </header>
  );
}
